/**
 * Invitation Acceptance Component
 * Handles invitation token validation and user registration/login
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const InvitationAcceptance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get token from URL
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  
  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Validate invitation token on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/invitations/validate/${token}`);
      const data = await response.json();
      
      if (!response.ok || !data.valid) {
        setError(data.error || 'Invalid invitation token');
        return;
      }
      
      setInvitation(data.invitation);
      setFormData(prev => ({
        ...prev,
        name: data.invitation.name || ''
      }));
      
    } catch (err) {
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (invitation?.invitationType === 'new_user') {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      
      if (!formData.name.trim()) {
        errors.name = 'Name is required';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAcceptInvitation = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch('/api/invitations/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password: formData.password,
          name: formData.name.trim()
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to accept invitation');
        return;
      }
      
      setSuccess(true);
      
      // Redirect to organization dashboard after a short delay
      setTimeout(() => {
        navigate(data.redirectUrl || `/org/${data.organization.slug}`);
      }, 2000);
      
    } catch (err) {
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Validating invitation...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="body1" color="text.secondary">
            The invitation link may have expired or is invalid. Please contact your organization administrator for a new invitation.
          </Typography>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon 
            sx={{ fontSize: 60, color: 'success.main', mb: 2 }} 
          />
          <Typography variant="h4" gutterBottom>
            Welcome aboard!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You've successfully joined {invitation?.organizationName}. 
            Redirecting you to your dashboard...
          </Typography>
          <CircularProgress size={30} />
        </Paper>
      </Container>
    );
  }

  // Main invitation acceptance form
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" mb={3}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            Join {invitation?.organizationName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You've been invited to join as a {invitation?.role}
          </Typography>
        </Box>

        {/* Invitation Details */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                <strong>Email:</strong> {invitation?.email}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                <strong>Role:</strong> {invitation?.role}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Form for new users */}
        {invitation?.invitationType === 'new_user' && (
          <>
            <Typography variant="h6" gutterBottom>
              Complete your registration
            </Typography>
            
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!formErrors.name}
              helperText={formErrors.name}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!formErrors.password}
              helperText={formErrors.password}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              type="password"
              label="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              margin="normal"
              required
            />
          </>
        )}

        {/* Form for existing users */}
        {invitation?.invitationType === 'existing_user' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              You already have an account. Accepting this invitation will add you to {invitation?.organizationName}.
            </Typography>
          </Alert>
        )}

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Action buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptInvitation}
            disabled={submitting}
            fullWidth
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Accepting...' : 'Accept Invitation'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />
        
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          By accepting this invitation, you agree to join {invitation?.organizationName} on the Situation Room platform.
        </Typography>
      </Paper>
    </Container>
  );
};

export default InvitationAcceptance;